/**
 * @file DeviceSession.ts
 * @description 单个设备连接的运行时上下文。
 * 它持有主通信通道、归一化 manifest，并提供按语义查找 HID 通道的入口。
 */

import type { ChannelDescriptor } from '../../channels/Channel'
import type { ChannelSelector } from '../../channels/ChannelRole'
import type { HidChannel } from '../../channels/hid/HidChannel'
import type { NormalizedDeviceManifest } from '../../manifests/ManifestTypes'
import { normalizeManifest } from '../../manifests/ManifestNormalizer'
import type { DeviceManifest } from '../../types/manifest'
import type { BaseDevice } from '../BaseDevice'
import { DeviceChannelManager } from './DeviceChannelManager'

/**
 * 设备会话构造参数。
 */
export interface DeviceSessionOptions {
  owner: BaseDevice
  manifest: DeviceManifest | NormalizedDeviceManifest
  primaryChannel: HidChannel
}

/**
 * 设备连接上下文。
 */
export class DeviceSession {
  public readonly manifest: NormalizedDeviceManifest
  /** 当前设备会话的统一通道入口。 */
  public readonly channels: DeviceChannelManager

  constructor(options: DeviceSessionOptions) {
    this.manifest =
      'channels' in options.manifest ? options.manifest : normalizeManifest(options.manifest)
    this.channels = new DeviceChannelManager(options.owner, this.manifest, options.primaryChannel)
  }

  /**
   * 注册运行期已打开的 HID 通道。
   */
  public registerChannel(selector: ChannelSelector, channel: HidChannel): void {
    this.channels.register(selector, channel)
  }

  /**
   * 获取已注册通道。当前第一阶段只保证主 control 通道可用。
   */
  public getChannel(selector: ChannelSelector = { usageType: 'default' }): HidChannel | undefined {
    return this.channels.get(selector)
  }

  /**
   * 根据语义查找 manifest 中声明的通道描述。
   */
  public resolveDescriptor(selector: ChannelSelector): ChannelDescriptor | undefined {
    return this.channels.resolve(selector)
  }
}
